
import { ReactNode } from 'react';

// Agregamos la interfaz global para CobrowseIO
declare global {
    interface Window {
        CobrowseIO?: {
            client: () => Promise<void>;
            start: () => Promise<void>;
            createSessionCode: () => Promise<string>;
            on: (event: string, callback: (session: any) => void) => void;
            stop: () => Promise<void>;
            license: string;
        };
        dataLayer?: any[];
        gtag?: (...args: any[]) => void;
    }
}

export interface SeoData {
    title: string;
    metaDescription: string;
    keywords: string; // comma-separated
    slug: string;
}

export interface QuantityPricePair {
    quantity: number | string; // Use string for input field compatibility
    price: number | string;
    deliveryTime?: string;
    orderCutoffTime?: string;
    urgency?: string; // New field for express delivery window
    urgencyPrice?: number | string; // New field for urgency price
    // Envío por cantidad: 200 piezas no pesan lo mismo que 25.
    // Si se dejan vacíos se usa el ShippingConfig del producto (compatibilidad).
    shippingWeight?: number | string; // kg reales de esta cantidad
    shippingPackagingId?: string; // embalaje para esta cantidad
    shippingPackageCount?: number | string; // nº de bultos (ej. 1000 pzas → 2 cajas)
}

export type FinishApplication = string;

export interface ProductFinish {
    name: string;
    application: FinishApplication;
    icon?: string;
}

export interface ProductMaterial {
  name: string;
  icon?: string;
}

export interface AiFeature {
  icon: string;
  text: string;
}

export interface InfoTableColumn {
  id: string;
  image: string;
  title:string;
}

export interface InfoTableRow {
  id: string;
  title: string;
  cells: { [key: string]: string[] }; // Key is column id
}
export interface InfoTableSection {
  type: 'infoTable';
  title: string;
  columns: InfoTableColumn[];
  rows: InfoTableRow[];
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
export interface FaqSection {
  type: 'faq';
  title: string;
  items: FaqItem[];
}

export interface FaqListSection {
  type: 'faqList';
  title: string;
  items: FaqItem[];
}


export interface MaterialInfoItem {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  iconClass?: string;
  colors?: string[];
}
export interface MaterialInfoSection {
  type: 'materialInfo';
  title: string; // This will be the accordion title e.g. "Materiales"
  items: MaterialInfoItem[];
}

export interface FinishInfoItem {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
}
export interface FinishInfoSection {
  type: 'finishInfo';
  title: string;
  items: FinishInfoItem[];
}

export type ContentSection = InfoTableSection | FaqSection | MaterialInfoSection | FinishInfoSection | FaqListSection;

// --- Shipping Interfaces ---
export type PackagingType = 'box' | 'envelope' | 'soft';

export interface Packaging {
    id: string;
    name: string;
    type: PackagingType;
    dimensions: {
        length: number;
        width: number;
        height: number;
    };
    weightEmpty: number; // kg
    isDefault: boolean;
}

export interface OriginAddress {
    id: string;
    name: string;
    address: string; // Full string for display
    street?: string;
    number?: string;
    district?: string;
    city?: string;
    state?: string;
    zip?: string; // Crucial for shipping rates
    isDefault: boolean;
}

export interface ShippingConfig {
    isPhysicalProduct: boolean;
    weight: number; // kg
    packagingId: string;
    originAddressId: string;
}
// ---------------------------

export interface Product {
  id: number;
  hidden?: boolean; // Borrador: true = invisible en el storefront, visible solo en el admin
  name: string;
  description: string;
  longDescription: string;
  price: number; // Base price, used for "From $" display
  costPrice?: number; // Internal: purchase/manufacturing cost
  imageUrls: string[];
  category: string;
  options?: { name: string; values: string[] }[]; // Added options property
  dimensions?: ProductDimensions;
  printOptions?: ProductPrintOptions[];
  materials?: ProductMaterial[];
  finishes?: ProductFinish[];
  quantityPricing?: QuantityPricePair[];
  seo?: SeoData;
  aiBulletPoints?: string[];
  aiAdditionalDescription?: string;
  aiFeatures?: AiFeature[];
  contentSections?: ContentSection[];
  printOptionIcons?: Record<string, string>;
  finishIcons?: Record<string, string>;
  productPageLayout?: PageSectionSetting[];
  shipping?: ShippingConfig; // Added shipping config
  imageAnchors?: Record<string, {
    materials?: string[];
    finishes?: string[];
    printSpecs?: string[];
  }>;
}

export interface ProductDimensions {
    width: number;
    height: number;
    cutWidth: number;
    cutHeight: number;
    safeWidth: number;
    safeHeight: number;
    folds?: { x: number; y: number }[];
}

export type PrintOption = 'Color' | 'Blanco y Negro' | 'Sin Impresión';

export interface ProductPrintOptions {
    front: PrintOption;
    back: PrintOption;
    icon?: string;
}

export enum OrderStatus {
  PendientePago = "Pendiente de Pago",
  Recibido = "Recibido",
  Produccion = "En Producción",
  Preparando = "Preparando Envío",
  EnRuta = "En Ruta",
  Entregado = "Entregado",
}

export interface ShippingLabel {
    trackingNumber: string;
    labelUrl: string;
    trackUrl: string;
    carrier: string;
    service: string;
    generatedAt: string; // ISO string
}

export interface Order {
  id: string; // The Firestore document ID
  userId: string;
  userEmail?: string; // The registered user's email
  createdAt: any; // Firestore Timestamp
  status: OrderStatus;
  items: CartItem[]; // Serialized version (without live File objects)
  shippingAddress: Address | null;
  billingAddress: Address;
  deliverySelections: Record<string, DeliveryOption | null>;
  totals: {
      subtotal: number;
      shipping: number;
      iva: number;
      total: number;
      discount?: number;
  };
  appliedCoupon?: string | null;
  payment: {
      method: string;
      transactionId: string;
      status: 'succeeded' | 'pending' | 'failed';
  };
  shippingLabel?: ShippingLabel; // Added shipping label info
  reviewRating?: number; // Stores the user's rating for this order
  generatedCouponCode?: string; // Stores the coupon code generated after reviewing this order
}


export interface DeliveryInfo {
  estimatedDeliveryString: string;
  estimatedDeliveryDate: Date;
  countdownTarget: Date;
  orderDeadlineString: string;
  isExpress: boolean;
  isUrgent: boolean;
}

export interface ReviewSolicitationSettings {
  couponCode: string;
  discountPercentage: number;
  whatsappMessage: string;
  emailMessage: string;
  activeSubRatings: string[];
  whatsappCouponMessage: string;
  emailCouponMessage: string;
  // Gamification Settings
  photoRewardPercentage: number; // e.g. 8%
  commentRewardPercentage: number; // e.g. 2%
}

export interface AnalyticsSettings {
    googleTagId: string;
}

export interface PaymentSettings {
    enableSpei: boolean;
    enablePaypal?: boolean;
}

export interface DesignServiceSettings {
    replicaDeliveryTime: string; // e.g. "24 horas", "1 día"
    customDesignDeliveryTime: string; // e.g. "2-3 días"
    replicaPrice: number;
    replicaNonEditableFee: number;
    customDesignPrice: number;
    customLogoRequestPrice: number;
    customFilesDownloadPrice: number;
    // Section toggles
    enableFreeDesign: boolean;
    enableUploadDesign: boolean;
    enableReplicaDesign: boolean;
    enableCustomDesign: boolean;
    // Free design field toggles
    freeDesignEnableLogo: boolean;
    freeDesignEnablePhoto: boolean;
    freeDesignEnableCustomColors: boolean;
}

export interface PageSectionSetting {
  id: string;
  title: string;
  visible: boolean;
}

export interface Category {
  id: string; // Document ID from Firestore
  name: string;
}

export interface WorkingHoursDay {
    day: string;
    dayIndex: number;
    enabled: boolean;
    start: string;
    end: string;
}

export interface HelpSettings {
    whatsappNumber: string;
    enableLiveSupport?: boolean; // Nuevo campo para activar/desactivar Co-browsing
    schedule: WorkingHoursDay[];
    holidays?: string[]; // Días festivos en formato YYYY-MM-DD, se tratan como inhábiles
    contactEmail?: string;
    contactPhone?: string;
    recentWorks?: GalleryItem[];
}

export interface GalleryItem {
    id: string;
    imageUrl: string;       // Foto de portada (miniatura en el grid)
    images?: string[];      // Fotos adicionales del proyecto (galería en lightbox)
    title?: string;
    description?: string;
    projectUrl?: string;    // URL externa del proyecto (abre en nueva pestaña)
}

export interface HelpRequest {
    id: string;
    name: string;
    message: string;
    contactType: 'email' | 'phone' | 'whatsapp';
    contactValue: string;
    timestamp: string;
    status: 'pending' | 'contacted' | 'resolved';
}

export interface MapCoverageConfig {
    centerName: string;
    radiusKm: number;
    lat: number;
    lng: number;
}

export interface ShippingSettings {
    freeShippingThreshold: number;
    coverageZips: string; // Comma-separated string of ZIP codes
    freeShippingDays: number;
    handlingDays?: number; // Días hábiles entre fin de producción y entrega a paquetería
    enablePickup?: boolean; // "Recoger en Sucursal"
    enableSelfExpress?: boolean; // "Envío Express por tu Cuenta" (Uber/Didi)
    coverageType?: 'zips' | 'map';
    mapConfig?: MapCoverageConfig;
}

export interface AutomaticDiscount {
  id: 'newCustomer' | 'volume';
  type: 'percentage';
  value: number;
  isActive: boolean;
  minAmount?: number;
  description: string;
  label: string;
}

export interface Coupon {
    id: string;
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
    isActive: boolean;
    scope: 'all' | 'category';
    categoryName?: string;
    minAmount?: number;
    maxUses?: number; // Total times this coupon can be used
    usedCount?: number; // How many times it has been used
}

export interface GeneralBannerDiscount {
    isActive: boolean;
    message: string;
    type: 'percentage';
    value: number;
    backgroundColor: string;
}

export interface DiscountSettings {
  automaticDiscounts: AutomaticDiscount[];
  coupons: Coupon[];
  generalBanner: GeneralBannerDiscount;
}

export interface BrandingSettings {
    platformName: string;
    platformNameAccent: string;
    faviconUrl: string;
    logoUrl: string;
    logoIcon: string;
    primaryColor: string;
    secondaryColor: string;
}

export interface ProductContextType {
    products: Product[];
    updateProduct: (updatedProduct: Product) => void;
    addProduct: (newProductData: Omit<Product, 'id'>) => void;
    deleteProduct: (productId: number) => Promise<void>;
    getProductById: (id: number) => Product | undefined;
    getProductBySlug: (categorySlug: string, productSlug: string) => Product | undefined;
    getProductsByCategory: (category: string, currentProductId?: number) => Product[];
    availableMaterials: string[];
    addMaterial: (material: string) => void;
    availableFinishes: string[];
    addFinish: (finish: string) => void;
    availableFinishApplications: string[];
    addFinishApplication: (application: string) => void;
    reviews: CustomerPhotoReview[];
    addReview: (newReviewData: Omit<CustomerPhotoReview, 'id' | 'status'>) => void;
    updateReview: (updatedReview: CustomerPhotoReview) => void;
    deleteReview: (reviewId: number) => void;
    updateReviewStatus: (reviewId: number, status: ReviewStatus) => void;
    reviewSettings: ReviewSolicitationSettings;
    updateReviewSettings: (settings: ReviewSolicitationSettings) => void;
    pageLayoutSettings: PageSectionSetting[];
    updatePageLayoutSettings: (settings: PageSectionSetting[]) => void;
    isLoading: boolean;
    categories: Category[];
    addCategory: (name: string) => Promise<void>;
    updateCategory: (category: Category) => Promise<void>;
    deleteCategory: (category: Category) => Promise<void>;
    helpSettings: HelpSettings;
    updateHelpSettings: (settings: HelpSettings) => void;
    helpRequests: HelpRequest[];
    addHelpRequest: (req: Omit<HelpRequest, 'id' | 'timestamp' | 'status'>) => Promise<void>;
    updateHelpRequest: (req: HelpRequest) => Promise<void>;
    shippingSettings: ShippingSettings;
    updateShippingSettings: (settings: ShippingSettings) => void;
    discountSettings: DiscountSettings;
    updateDiscountSettings: (settings: DiscountSettings) => void;
    // Shipping Config
    packagingOptions: Packaging[];
    addPackaging: (packaging: Omit<Packaging, 'id'>) => void;
    updatePackaging: (packaging: Packaging) => void;
    deletePackaging: (id: string) => void;
    originAddresses: OriginAddress[];
    addOriginAddress: (address: Omit<OriginAddress, 'id'>) => void;
    updateOriginAddress: (address: OriginAddress) => void;
    deleteOriginAddress: (id: string) => void;
    // Analytics
    analyticsSettings: AnalyticsSettings;
    updateAnalyticsSettings: (settings: AnalyticsSettings) => void;
    // Design Services
    designServiceSettings: DesignServiceSettings;
    updateDesignServiceSettings: (settings: DesignServiceSettings) => void;
    // Branding
    brandingSettings: BrandingSettings;
    updateBrandingSettings: (settings: BrandingSettings) => Promise<void>;
    // Payments
    paymentSettings: PaymentSettings;
    updatePaymentSettings: (settings: PaymentSettings) => Promise<void>;
}

export interface ProductProviderProps {
    children: ReactNode;
}

export interface SubRating {
  label: string;
  rating: number;
}

export enum ReviewStatus {
  Pendiente = 'pendiente',
  Aprobada = 'aprobada',
  Rechazada = 'rechazada',
}

export interface CustomerPhotoReview {
  id: number;
  name: string;
  initial: string;
  country: string;
  overallRating: number;
  date: string;
  comment: string;
  subRatings: SubRating[];
  images: string[];
  status: ReviewStatus;
}

export interface UploadedFileInfo {
    file: File;
    pdfPage?: number;
    rotation?: number;
    fit?: 'contain' | 'horizontal' | 'vertical';
    orientation?: 'horizontal' | 'vertical'; // User selected orientation
}

export interface PersistedFileInfo {
  name: string;
  type: string;
  size: number;
  pdfPage?: number;
  dataUrl?: string;
  rotation?: number;
  fit?: 'contain' | 'horizontal' | 'vertical';
  orientation?: 'horizontal' | 'vertical'; // User selected orientation
}

export type DesignWorkflowStatus = 'pending_design' | 'proposal_ready' | 'proof_ready' | 'changes_requested' | 'approved';

export interface DesignWorkflow {
    status: DesignWorkflowStatus;
    proofUrl?: string; // URL of the selected/active design proof image (legacy, kept for compat)
    proofFrontUrl?: string; // URL of the front side proof
    proofBackUrl?: string; // URL of the back side proof
    proposals?: string[]; // Array of initial proposal URLs (for custom design selection)
    selectedProposalIndex?: number | null; // Which proposal was selected
    revisionsUsed: number; // Current number of revisions (max 3)
    maxRevisions: number; // Limit (usually 3)
    // AI generation state (persisted to survive close/tab switch)
    aiFrontUrl?: string; // Generated front Firebase Storage URL
    aiBackUrl?: string; // Generated back Firebase Storage URL
    history: {
        date: string; // ISO String
        action: 'proposal_uploaded' | 'proof_uploaded' | 'changes_requested' | 'approved' | 'proposal_selected';
        comment?: string;
        url?: string; // URL of the file associated with this action
        side?: 'front' | 'back' | 'manual'; // Which side was sent
    }[];
}

export interface FreeDesignTemplate {
    id: string;
    title: string;
    preview: string | null;
    color: string | null;
    style: string;
    isSample: boolean;
    freepikUrl?: string | null;
}

export interface CartDesignInfo {
    type: 'upload' | 'replica' | 'custom' | 'freeDesign';
    wantsProof?: boolean;
    // For runtime use, contains non-serializable File objects
    files?: { [key: string]: UploadedFileInfo | null };
    // For localStorage persistence, contains serializable file metadata
    persistedFiles?: { [key: string]: PersistedFileInfo | null };
    comments?: string;
    replicaData?: any;
    customData?: any;
    freeDesignData?: {
        businessCategory: string;
        orientation: string;
        selectedTemplate: FreeDesignTemplate | null;
        contactInfo: {
            nombre: string;
            cargo: string;
            empresa: string;
            telefono: string;
            email: string;
            web: string;
            direccion: string;
        };
        useCustomColors: boolean;
        colorPrimario?: string;
        colorSecundario?: string;
    };
}

export interface CartItem {
  id: string; // Unique ID for this cart item instance
  productId: number;
  selectedOptions: { [key: string]: string };
  selectedPriceInfo: QuantityPricePair;
  isUrgencyActive: boolean;
  designInfo: CartDesignInfo | null;
  designServicePrice: number;
  designWorkflow?: DesignWorkflow; // New field for replica/custom workflow
}

export interface Address {
  id: string;
  userId: string;
  name: string;
  streetName: string;
  streetNumber: string;
  apartment?: string;
  neighborhood: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  rfc?: string;
  reference?: string;
}

export interface DeliveryOption {
    id: string;
    quote_id?: string;
    name: string;
    date: string;
    price: number;
    isPopular?: boolean;
    icon: string;
    description: string;
    carrier?: string;
    service?: string;
    carrierId?: string | number;
    serviceId?: string | number;
}

// ──────────────────────────────────────────────
// Hero Carousel Types
// ──────────────────────────────────────────────

export type KenBurnsEffect = 'zoomIn' | 'zoomOut' | 'none';
export type CarouselTransition = 'fade' | 'slideLeft' | 'slideUp';

export interface HeroSlide {
    id: string;
    imageUrl: string;
    alt: string;
    label?: string;
    effect: KenBurnsEffect;
}

export interface HeroCarouselConfig {
    slides: HeroSlide[];
    intervalSeconds: number;
    transition: CarouselTransition;
    enabled: boolean;
}
